import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaExclamationTriangle } from 'react-icons/fa';
import { authAPI } from '../../api';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Basic validation
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    // Check admin credentials from environment variables
    const adminCredentials = [
      {
        username: process.env.REACT_APP_ADMIN_1_USERNAME,
        password: process.env.REACT_APP_ADMIN_1_PASSWORD
      },
      {
        username: process.env.REACT_APP_ADMIN_2_USERNAME,
        password: process.env.REACT_APP_ADMIN_2_PASSWORD
      },
      {
        username: process.env.REACT_APP_ADMIN_3_USERNAME,
        password: process.env.REACT_APP_ADMIN_3_PASSWORD
      }
    ];

    // Check if credentials match any admin user
    const adminMatch = adminCredentials.find(
      cred => cred.username === formData.username && cred.password === formData.password
    );

    if (adminMatch) {
      // Admin login - create JWT-like token
      const token = btoa(JSON.stringify({
        username: adminMatch.username,
        isAdmin: true,
        timestamp: new Date().getTime()
      }));
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUsername', adminMatch.username);
      setIsLoading(false);
      navigate('/admin/dashboard');
      return;
    }

    // If not admin, try regular user login
    const result = await authAPI.login({
      username: formData.username,
      password: formData.password
    });

    setIsLoading(false);

    if (result.success) {
      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify(result.user));
      navigate('/dashboard');
    } else {
      setError(result.message || 'Login failed. Please try again.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-page-header">
        <div className="login-container">
          <h1 className="login-page-title">
            <FaUser />
            Login to IIITDM Conference 2026
          </h1>
          <p className="login-page-subtitle">Access your conference account</p>
        </div>
      </div>

      <div className="login-section">
        <div className="login-container">
          <div className="login-form-wrapper">
            <div className="login-card">
              <div className="login-card-header">
                <h2 className="login-card-title">Welcome Back!</h2>
                <p className="login-card-subtitle">Please log in to your account to continue</p>
              </div>

              <form className="login-form" onSubmit={handleSubmit}>
                {error && (
                  <div className="login-error-message">
                    <FaExclamationTriangle />
                    <span>{error}</span>
                  </div>
                )}

                <div className="login-form-group">
                  <label htmlFor="username" className="login-form-label">Username</label>
                  <div className="login-input-group">
                    <FaUser className="login-input-icon" />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="login-input"
                    />
                  </div>
                </div>

                <div className="login-form-group">
                  <label htmlFor="password" className="login-form-label">Password</label>
                  <div className="login-input-group">
                    <FaLock className="login-input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="login-input"
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="login-form-options">
                  <Link to="/forgot-password" className="login-forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>

                <button 
                  type="submit" 
                  className={`login-btn login-btn-primary login-btn-submit ${isLoading ? 'login-btn-loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="login-spinner"></div>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <FaUser />
                      Log In
                    </>
                  )}
                </button>
              </form>

              <div className="login-card-footer">
                <p className="login-card-footer-text">Don't have an account?</p>
                <Link to="/registration" className="login-register-link">
                  Sign up for Conference
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
